import { analytics } from "./analytics";

const toAmount = (value: string | number | undefined): number | undefined => {
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? (n as number) : undefined;
};

/**
 * Tracking facade (WEB-4044): same call sites as the Countly era, OpenPanel
 * engine + object_action taxonomy underneath. Checkout methods maintain the
 * checkout_completed flow so the conclusion carries duration_ms, and arm /
 * flush the dedicated revenue stream.
 */
class PageTrackingService {
  // ── Product ────────────────────────────────────────────────────────────────
  public trackProductView(productId: string, productTitle: string) {
    analytics.track("product_viewed", {
      product_id: productId,
      product_title: productTitle,
    });
  }

  public trackProductAddToCart(
    productId: string,
    productTitle: string,
    source = "product_page"
  ) {
    analytics.track("cart_item_added", {
      product_id: productId,
      product_title: productTitle,
      source,
    });
  }

  public trackProductAddToWishlist(productId: string, productTitle: string) {
    analytics.track("wishlist_added", {
      product_id: productId,
      product_title: productTitle,
    });
  }

  public trackProductRemoveFromWishlist(
    productId: string,
    productTitle: string
  ) {
    analytics.track("wishlist_removed", {
      product_id: productId,
      product_title: productTitle,
    });
  }

  public trackProductBuyNow(productId: string, productTitle: string) {
    analytics.track("buy_now_clicked", {
      product_id: productId,
      product_title: productTitle,
    });
  }

  // ── Cart ──────────────────────────────────────────────────────────────────
  public trackCartView(itemsCount: number, totalAmount: string) {
    analytics.track("cart_viewed", {
      items_count: itemsCount,
      amount: toAmount(totalAmount),
    });
  }

  public trackCartItemRemove(productId: string, productTitle: string) {
    analytics.track("cart_item_removed", {
      product_id: productId,
      product_title: productTitle,
    });
  }

  /** "Proceed to checkout" — opens the checkout flow (one per attempt). */
  public trackCartCheckout(itemsCount: number, totalAmount: string) {
    analytics.track("checkout_started", {
      items_count: itemsCount,
      amount: toAmount(totalAmount),
      source: "cart",
    });
    analytics.startFlow("checkout_completed", {
      amount: toAmount(totalAmount),
    });
  }

  // ── Checkout ──────────────────────────────────────────────────────────────
  /**
   * Checkout page landed with an order. Deep links (/checkout?coupon_code=…)
   * skip the cart click, so open the flow here when it isn't running yet.
   * The coupon param means the loader applied a coupon server-side.
   */
  public trackCheckoutView(orderId: string, totalAmount: string) {
    if (!analytics.hasFlow("checkout_completed")) {
      analytics.track("checkout_started", {
        amount: toAmount(totalAmount),
        source: "direct",
      });
      analytics.startFlow("checkout_completed");
    }
    const couponFromUrl = new URLSearchParams(window.location.search).get(
      "coupon_code"
    );
    analytics.updateFlow("checkout_completed", {
      order_id: orderId,
      amount: toAmount(totalAmount),
      ...(couponFromUrl ? { coupon_used: true } : {}),
    });
  }

  public trackPaymentMethodSelect(paymentMethod: string) {
    analytics.track("payment_method_selected", { method: paymentMethod });
    analytics.updateFlow("checkout_completed", {
      payment_method: paymentMethod,
    });
  }

  public trackCouponApply(couponCode: string, success: boolean) {
    analytics.track("coupon_applied", {
      coupon_code: couponCode,
      success,
    });
    if (success) {
      analytics.updateFlow("checkout_completed", { coupon_used: true });
    }
  }

  public trackCouponRemoved(couponCode: string) {
    analytics.track("coupon_removed", { coupon_code: couponCode });
    analytics.updateFlow("checkout_completed", { coupon_used: false });
  }

  /** Arm the revenue stream right before handing off to the gateway. */
  public trackPaymentInitiated(
    orderId: string | undefined,
    amount: string | number | undefined,
    paymentMethod?: string
  ) {
    const value = toAmount(amount);
    analytics.track("payment_initiated", {
      order_id: orderId,
      amount: value,
      payment_method: paymentMethod,
    });
    if (value !== undefined) {
      analytics.pendingRevenue(value, {
        order_id: orderId,
        payment_method: paymentMethod,
        channel: "store",
      });
    }
  }

  /**
   * Confirmed purchase — fires on the /orders?order_id=… return from the
   * payment gateway (the only client-side success signal). Concludes the
   * checkout flow and records real revenue from the matched order.
   */
  public trackPurchaseConfirmed(
    orderId: string,
    amount: string | number | undefined,
    currency?: string
  ) {
    const value = toAmount(amount);
    analytics.endFlow("checkout_completed", {
      order_id: orderId,
      ...(value !== undefined ? { amount: value } : {}),
      ...(currency ? { currency } : {}),
    });
    if (value !== undefined) {
      // Real amount known — replace any pendingRevenue armed at the gateway
      // handoff so the purchase can't double-count.
      analytics.clearRevenue();
      analytics.revenue(value, {
        order_id: orderId,
        ...(currency ? { currency } : {}),
        channel: "store",
      });
    } else {
      // Order not in the list yet (gateway returned before the order
      // materialized) — send the pending amount armed at payment_initiated
      // instead of losing the purchase.
      analytics.flushRevenue();
    }
  }

  public trackPaymentFailed(
    orderId: string | undefined,
    paymentMethod: string | undefined,
    reason?: string
  ) {
    analytics.track("checkout_failed", {
      order_id: orderId,
      payment_method: paymentMethod,
      reason,
    });
    analytics.cancelFlow("checkout_completed");
    analytics.clearRevenue();
  }

  // ── KYC (WEB-4029) ────────────────────────────────────────────────────────
  public trackKycPrompted(orderId?: string, amount?: string | number) {
    analytics.track("kyc_prompted", {
      order_id: orderId,
      amount: toAmount(amount),
    });
  }

  public trackKycSubmitted(result: "success" | "failure", reason?: string) {
    analytics.track("kyc_submitted", { result, reason });
  }

  public trackKycSkipped() {
    analytics.track("kyc_skipped", {});
  }

  // ── Search ────────────────────────────────────────────────────────────────
  public trackSearchView(searchQuery: string, resultsCount: number) {
    analytics.track("search_performed", {
      query: searchQuery,
      results_count: resultsCount,
    });
  }

  /** @deprecated folded into trackSearchView (search_performed). */
  public trackSearchQuery(_searchQuery: string) {
    // intentionally empty — search_performed carries the query + results
  }

  public trackSearchResultClick(
    productId: string,
    productTitle: string,
    position: number
  ) {
    analytics.track("search_result_clicked", {
      product_id: productId,
      product_title: productTitle,
      position,
    });
  }

  // ── Account / orders ──────────────────────────────────────────────────────
  public trackProfileView() {
    analytics.track("profile_viewed", {});
  }

  public trackOrderHistoryView(ordersCount: number) {
    analytics.track("orders_viewed", { orders_count: ordersCount });
  }

  public trackOrderDetailsView(orderId: string) {
    analytics.track("order_details_viewed", { order_id: orderId });
  }

  public trackWishlistView(itemsCount: number) {
    analytics.track("wishlist_viewed", { items_count: itemsCount });
  }
}

export const pageTrackingService = new PageTrackingService();
